/**
 * Keeps a "·" separator from ending a line, hides one that would start a line (pre-review r3, 2026-10-25), and marks the file names too long for one line (Matt 2026-10-24, ux-expert: results headings, the footer's version line and
 * "Also checked (4): …" showed "·" alone at a line end): in every text node, each " · " has its "·" wrapped together with the
 * word (or short element, a button, a file name) after it in a no-wrap span, so the separator travels with it. The text
 * itself is unchanged (a no-break space would change it, and does not hold against an inline-block). The "·" is its own
 * `.sep` span; one that lands first on a line (the word after it did not fit beside the one before) gets `sep-lead` and is not
 * drawn (styles.css), so no line starts with a bullet; measured after each render, on resize and when the fonts are in.
 * Run on the page after each render (src/ui/app.ts).
 */
export function glueSeparators(root: ParentNode): void {
  if (!resizing) {
    resizing = true;
    window.addEventListener('resize', () => { fitNames(document); markLeadingSeparators(document); });
    void document.fonts?.ready.then(() => markLeadingSeparators(document));
    // A fold opened later shows separators that had no size when they were measured.
    document.addEventListener('toggle', () => requestAnimationFrame(() => requestAnimationFrame(() => markLeadingSeparators(document))), true);
  }
  const walker = document.createTreeWalker(root as Node, NodeFilter.SHOW_TEXT);
  const hits: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if ((n.nodeValue ?? '').includes(' · ') && !(n.parentElement?.closest('textarea, script, style, pre, .nobreak, .build-stripe'))) hits.push(n as Text);
  }
  for (const first of hits) {
    let t: Text | null = first;
    while (t) {
      const v: string = t.nodeValue ?? '';
      const i = v.indexOf(' · ');
      if (i < 0) break;
      const tail: Text = t.splitText(i + 1); // starts at "· …"
      const afterSep: Text = tail.splitText(1); // " word …" (the "·" alone is `tail`)
      const word = /^\S+/.exec((afterSep.nodeValue ?? '').slice(1))?.[0] ?? '';
      const wrap = document.createElement('span');
      wrap.className = 'nobreak';
      const sepEl = document.createElement('span');
      sepEl.className = 'sep';
      tail.before(wrap);
      sepEl.append(tail);
      wrap.append(sepEl);
      if (word) {
        const after: Text = afterSep.splitText(1 + word.length);
        wrap.append(afterSep);
        t = after;
      } else {
        // The separator ends this text: it goes with the element that follows.
        const next: ChildNode | null = afterSep.nextSibling;
        wrap.append(afterSep);
        if (next instanceof HTMLElement && ((next.textContent ?? '').length <= 24 || (next.tagName === 'BDI' && next.getAttribute('dir') === 'ltr'))) wrap.append(next);
        t = null;
      }
    }
  }
  fitNames(root);
  markLeadingSeparators(root);
}

let resizing = false;

/**
 * A file name (an LTR run) stays on one line (styles.css `bdi[dir=ltr]`): one that is longer than 28 characters, or that does
 * not fit the width of its block (a line that cannot break before it), may break anywhere instead of running out of the page.
 */
function fitNames(root: ParentNode): void {
  for (const b of root.querySelectorAll('bdi[dir="ltr"]')) {
    if (b.classList.contains('long-name')) continue;
    let block = b.parentElement;
    while (block && getComputedStyle(block).display.startsWith('inline')) block = block.parentElement;
    const edge = block?.getBoundingClientRect();
    const box = b.getBoundingClientRect();
    // Past the block's trailing edge or the page's (right in LTR, left in RTL).
    const past = box.width > 0 && (Math.round(box.right) > Math.round(Math.min(edge?.right ?? Infinity, document.documentElement.clientWidth)) || Math.round(box.left) < Math.round(Math.max(edge?.left ?? -Infinity, 0)));
    if ((b.textContent ?? '').length > 28 || past) b.classList.add('long-name');
  }
}

/**
 * A separator that is the first thing on its line (the word after it did not fit beside the one before) is removed: its run
 * gets `sep-lead` (styles.css: the "·" not displayed and a forced break where the run starts, which it already did), so no line
 * starts with a bullet, the words are at the start edge with no gap, and none jumps back up behind the removed bullet.
 */
export function markLeadingSeparators(root: ParentNode): void {
  const runs = [...root.querySelectorAll<HTMLElement>('.sep')].map((e) => e.parentElement).filter((e): e is HTMLElement => e instanceof HTMLElement);
  for (const r of runs) {
    r.classList.remove('sep-lead');
  }
  // Removing a bullet and breaking the line there moves the lines below: measured again until none is left at a line start.
  const marked: HTMLElement[] = [];
  for (let pass = 0; pass < 8; pass++) {
    const lead = runs.filter((run) => !run.classList.contains('sep-lead') && startsLine(run));
    if (!lead.length) break;
    for (const run of lead) run.classList.add('sep-lead');
    marked.push(...lead);
  }
  // A break made for one run can bring a later one back to mid-line: a mark that is no longer needed is taken off again (one by one).
  for (const run of marked) {
    run.classList.remove('sep-lead');
    if (startsLine(run)) run.classList.add('sep-lead');
  }
}

/** The run's "·" is displayed and is the first thing on its line: the previous text on the page ends on a line above it. */
function startsLine(run: HTMLElement): boolean {
  const sep = run.querySelector<HTMLElement>(':scope > .sep');
  if (!sep) return false;
  const me = sep.getBoundingClientRect();
  if (!me.width && !me.height) return false; // not on screen (a closed fold)
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  walker.currentNode = sep.firstChild ?? sep;
  let prev: DOMRect | undefined;
  for (let n = walker.previousNode(); n && !prev; n = walker.previousNode()) {
    if (!(n.nodeValue ?? '').trim() || n.parentElement?.closest('[hidden], .visually-hidden, script, style')) continue;
    const r = document.createRange();
    r.selectNodeContents(n);
    prev = [...r.getClientRects()].filter((q) => q.width > 0).pop();
  }
  return Boolean(prev) && me.top > (prev as DOMRect).bottom - 2 && (prev as DOMRect).top < me.top - 4;
}

/**
 * The diagnostic line's two parts: the question (up to and with its first question mark: ?, ？ or ؟) and the action, with the
 * original gap between them (a space, or none in Chinese). `q + gap + a` is always the full text, so the button's name is the
 * whole string. A text without a question mark, or with nothing after it, stays one piece (undefined).
 */
export function splitQuestion(text: string): { q: string; gap: string; a: string } | undefined {
  const m = /^(.*?[?？؟])(\s*)(\S.*)$/s.exec(text);
  return m ? { q: m[1] ?? '', gap: m[2] ?? '', a: m[3] ?? '' } : undefined;
}
